import { useCallback } from 'react';
import { StaffMember, StaffRole, GameNotification } from '../types';
import { STAFF_ROLE_CONFIGS, STAFF_HIRE_SIGNING_BONUS_MONTHS } from '../constants';

interface UseStaffManagementProps {
  staff: StaffMember[];
  setStaff: React.Dispatch<React.SetStateAction<StaffMember[]>>;
  money: number;
  setMoney: React.Dispatch<React.SetStateAction<number>>;
  gameTime: number;
  triggerNotification: (message: string, type: GameNotification['type'], duration?: number) => void;
}

export interface StaffManagementHook {
  hireStaff: (role: StaffRole) => boolean;
  fireStaff: (id: string) => void;
  getStaffCount: (role: StaffRole) => number;
  getDailyStaffCost: () => number;
  /** Revenue multiplier from network engineers (e.g. 1.30 for 2 engineers) */
  getNetworkEngineerBonus: () => number;
  /** Event chance reduction from security officers (e.g. 0.40 for 2 officers) */
  getSecurityReduction: () => number;
  /** Power upgrade cost multiplier from electricians (e.g. 0.80 for 2 electricians) */
  getElectricianDiscount: () => number;
}

export const useStaffManagement = ({
  staff,
  setStaff,
  money,
  setMoney,
  gameTime,
  triggerNotification,
}: UseStaffManagementProps): StaffManagementHook => {

  const getStaffCount = useCallback(
    (role: StaffRole) => staff.filter(s => s.role === role).length,
    [staff]
  );

  const hireStaff = useCallback((role: StaffRole): boolean => {
    const config = STAFF_ROLE_CONFIGS.find(c => c.role === role);
    if (!config) return false;

    const current = staff.filter(s => s.role === role).length;
    if (current >= config.maxCount) {
      triggerNotification(`Maximum ${config.name}s already hired (${config.maxCount}).`, 'warning');
      return false;
    }

    const signingBonus = config.monthlySalary * STAFF_HIRE_SIGNING_BONUS_MONTHS;
    if (money < signingBonus) {
      triggerNotification(
        `Need $${signingBonus.toLocaleString()} signing bonus to hire a ${config.name}.`,
        'error'
      );
      return false;
    }

    const names = [
      'Alex', 'Jordan', 'Morgan', 'Taylor', 'Casey', 'Riley', 'Drew', 'Avery',
      'Skyler', 'Quinn', 'Blake', 'Cameron', 'Dana', 'Finley', 'Hayden', 'Jamie',
    ];
    const newMember: StaffMember = {
      id: `staff-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      role,
      name: names[Math.floor(Math.random() * names.length)],
      hiredAt: gameTime,
    };

    setMoney(prev => prev - signingBonus);
    setStaff(prev => [...prev, newMember]);
    triggerNotification(
      `Hired ${newMember.name} as ${config.name} ($${signingBonus.toLocaleString()} signing bonus).`,
      'success'
    );
    return true;
  }, [staff, money, gameTime, setMoney, setStaff, triggerNotification]);

  const fireStaff = useCallback((id: string) => {
    setStaff(prev => {
      const member = prev.find(s => s.id === id);
      if (member) {
        const config = STAFF_ROLE_CONFIGS.find(c => c.role === member.role);
        triggerNotification(`${member.name} (${config?.name ?? member.role}) has been let go.`, 'info');
      }
      return prev.filter(s => s.id !== id);
    });
  }, [setStaff, triggerNotification]);

  const getDailyStaffCost = useCallback((): number => {
    return staff.reduce((total, member) => {
      const config = STAFF_ROLE_CONFIGS.find(c => c.role === member.role);
      return total + (config ? config.monthlySalary / 30 : 0);
    }, 0);
  }, [staff]);

  const getNetworkEngineerBonus = useCallback((): number => {
    const count = staff.filter(s => s.role === 'network_engineer').length;
    return 1 + count * 0.15;
  }, [staff]);

  const getSecurityReduction = useCallback((): number => {
    const count = staff.filter(s => s.role === 'security_officer').length;
    return Math.min(0.8, count * 0.20); // cap at 80% reduction
  }, [staff]);

  const getElectricianDiscount = useCallback((): number => {
    const count = staff.filter(s => s.role === 'electrician').length;
    return Math.max(0.7, 1 - count * 0.10); // cap discount at 30%
  }, [staff]);

  return {
    hireStaff,
    fireStaff,
    getStaffCount,
    getDailyStaffCost,
    getNetworkEngineerBonus,
    getSecurityReduction,
    getElectricianDiscount,
  };
};
